import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  styled,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { logger } from "@platform/browser-logger";
import { Button } from "@platform/design-system";

import { RfpContext } from "../../../contexts";
import { RFP_ACTION_TYPES } from "../../../constants";
import { sortProductsByCoverage } from "../../../../../pages/RFP/utils";

const { UPDATE_STATE_RELATED_DOCUMENTS, EDIT_STATE_RELATED_DOCUMENTS } =
  RFP_ACTION_TYPES;

const mapCoverages = (list, fileCoverages) =>
  list.map((coverage) => ({
    isChecked: fileCoverages.includes(coverage.coverageName),
    coverageName: coverage.coverageName,
    alreadyAssigned: coverage.files.length > 0,
    coverageId: coverage.coverageInfo?.id,
    required: coverage.required,
  }));

const AssignCoverages = ({
  file,
  uploadedFiles,
  documentType,
  closeModal,
  fileCoverages,
  isEdditing = false,
}) => {
  const { handleSaveDraft, isSavingDraftRfp, setRfp } = useContext(RfpContext);
  const fullyList = [
    ...documentType.coverages.required,
    ...documentType.coverages.optional,
  ];

  // TODO(eslint): fix
  // eslint-disable-next-line prefer-const
  let [selectedCoverages, setSelectedCoverages] = useState(
    mapCoverages(fullyList, fileCoverages),
  );

  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const allChecked = selectedCoverages.every((coverage) => coverage.isChecked);
  const refBarrier = useRef(false);

  const refFileCoverages = useRef(fileCoverages);
  selectedCoverages = sortProductsByCoverage(selectedCoverages, "coverageName");

  useEffect(() => {
    const saveDraft = async () => {
      try {
        await handleSaveDraft();
        setIsReadyToSave(false);
        refBarrier.current = false;
        closeModal();
      } catch (error) {
        logger.error("Saving draft error", error);
      }
    };
    if (isReadyToSave && refBarrier.current === false) {
      refBarrier.current = true;
      saveDraft();
    }
  }, [closeModal, handleSaveDraft, isReadyToSave]);

  const onSelectCoverage = (type, coverage, checked) => {
    if (type === "all") {
      setSelectedCoverages(
        selectedCoverages.map((item) => ({ ...item, isChecked: !allChecked })),
      );
    } else {
      setSelectedCoverages(
        selectedCoverages.map((elem) =>
          elem.coverageId !== coverage.coverageId
            ? elem
            : { ...elem, isChecked: checked },
        ),
      );
    }
  };

  const getDocumentObj = (required, updatedValues = {}) => ({
    name: documentType.documentTypeName,
    required,
    selected: false,
    file,
    fileName: file.name,
    size: file.size,
    type: documentType.documentTypeName,
    ...updatedValues,
  });

  const getContextValue = () => {
    const checkedCoverages = selectedCoverages.filter((item) => item.isChecked);
    const uncheckedCoverages = selectedCoverages.filter(
      (item) => !item.isChecked,
    );
    const incomingCoverages = checkedCoverages.reduce((acc, coverage) => {
      const findCoverage = fullyList.find(
        (item) => item.coverageInfo.id === coverage.coverageId,
      );

      const initialDocs = findCoverage.coverageInfo?.documents || [];

      if (
        initialDocs.some(
          (item) =>
            item.fileName === file.name &&
            item.name === documentType.documentTypeName,
        )
      )
        return acc;

      const updatedDocuments = [
        ...initialDocs,
        getDocumentObj(coverage.required),
      ];

      return [
        ...acc,
        {
          ...findCoverage.coverageInfo,
          documents: updatedDocuments,
        },
      ];
    }, []);

    const currentFiles = uploadedFiles.map((item) => ({
      ...item.file,
      type: item.documentTypes ? Object.keys(item.documentTypes)[0] : null,
    }));
    const existFile = currentFiles.find((item) => item.name === file.name);
    const incomingDocs = existFile ? currentFiles : [...currentFiles, file];
    return {
      incomingCoverages,
      incomingDocs,
      uncheckedCoverages,
      fileName: file.name,
      documentTypeName: documentType.documentTypeName,
    };
  };

  const onSubmit = () => {
    const contextValue = getContextValue();

    setRfp({
      type: isEdditing
        ? EDIT_STATE_RELATED_DOCUMENTS
        : UPDATE_STATE_RELATED_DOCUMENTS,
      value: contextValue,
    });

    setIsReadyToSave(true);
  };

  const onSave = () => {
    onSubmit();
  };

  const onClose = () => {
    closeModal();
  };

  const bodyModal = () => (
    <>
      <DialogContentText>
        Select any coverages that <b>{file.name}</b> is for —
      </DialogContentText>
      <Box display="flex" flexDirection="column" mb={3}>
        <StyledFormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              onChange={() => onSelectCoverage("all")}
            />
          }
          label="Select All"
          isAll
        />
        {selectedCoverages.map((coverage) => (
          <StyledFormControlLabel
            key={coverage.coverageName}
            control={
              <Checkbox
                checked={coverage.isChecked}
                onChange={({ target: { checked } }) =>
                  onSelectCoverage("single", coverage, checked)
                }
                value={coverage.coverageName}
              />
            }
            label={coverage.coverageName}
          />
        ))}
      </Box>
    </>
  );

  const isBlockedCancel = isSavingDraftRfp || refBarrier.current;

  const disableSave = () => {
    if (!selectedCoverages.some((item) => item.isChecked)) return true;
    if (!isEdditing) return false;
    const initialChecked = refFileCoverages.current;
    const currentChecked = selectedCoverages
      .filter((item) => item.isChecked)
      .map((item) => item.coverageName);
    return (
      initialChecked.length === currentChecked.length &&
      initialChecked.every((item) => currentChecked.includes(item))
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={!!file}
      onClose={isBlockedCancel ? null : closeModal}
    >
      <DialogTitle>Assign Coverages</DialogTitle>
      <DialogContent>{bodyModal()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="tertiary" disabled={isBlockedCancel}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="primary"
          disabled={disableSave()}
          loading={isSavingDraftRfp}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => !["alreadyAssigned", "isAll"].includes(prop),
})(({ theme, alreadyAssigned, isAll }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.neutrals.neutral_300}`,
  marginInline: 0,
  ...(alreadyAssigned && { backgroundColor: "#fff4e5" }),
  ...(isAll && { ".MuiTypography-root": { fontWeight: "600" } }),
}));

export default AssignCoverages;
